import { useRef, useState, useEffect } from 'react'

import MediaRecorder from 'utils/MediaRecorder'
import RTCRecorder from 'utils/RTCRecorder'
import AudioDetector from 'utils/AudioDetector'

let audioRecorder = undefined

const getAudioRecorder = () => new Promise((resolve, reject) => {
  if (audioRecorder) {
    return resolve(audioRecorder)
  }

  const options = { audio: true }

  const onSucces = (stream) => {
    let recorder = null

    if (window.MediaRecorder) {
      recorder = new MediaRecorder(stream)
    } else if (!window.MediaRecorder) {
      recorder = new RTCRecorder(stream)
    }

    if (recorder) {
      audioRecorder = recorder
      resolve(recorder)
    } else if (!recorder) {
      reject('Recorder not initalised')
    }
  }

  const onError = (error) => {
    reject(error)
  }

  if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    // Safari requires to call getUserMedia directly on the MediaDevices object
    navigator.mediaDevices.getUserMedia(options).then(onSucces).catch(onError)
  } else {
    navigator.getUserMedia = (
      navigator.getUserMedia
      || (navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      || navigator.mozGetUserMedia
      || navigator.msGetUserMedia
      || navigator.webkitGetUserMedia
    )

    if (navigator.getUserMedia === undefined) {
      return reject('User Media is not supported');
    }

    navigator.getUserMedia(options).then(onSucces).catch(onError)
  }
})

export default ({
  onStop = () => {},
  onError = () => {},
} = {}) => {
  const instance = useRef()
  const levelListener = useRef()
  const levelDetector = useRef()
  const [level, setLevel] = useState(null)
  const [isRecording, setIsRecording] = useState(null)
  const [analyserNode, setAnalyserNode] = useState(null)
  const [ready, setReady] = useState(false)

  const record = (onOff) => setIsRecording(onOff)

  useEffect(() => {
    getAudioRecorder()
      .then((recorder) => {
        instance.current = recorder

        const ctx = recorder.getContext()
        if (ctx) {
          const analyser = ctx.createAnalyser()
          analyser.fftSize = 256
          ctx.createMediaStreamSource(recorder.getStream()).connect(analyser)
          setAnalyserNode(analyser)
        }

        levelDetector.current = new AudioDetector(
          instance.current.getStream(),
          instance.current.getContext()
        ).start()

        setReady(true)
      })
      .catch((error) => console.log({ error }))

    return () => {
      if (levelListener && levelListener.current) {
        clearInterval(levelListener.current)
      }

      if (instance.current &&
        instance.current.adapter &&
        instance.current.adapter.state != 'inactive'
      ) {
        instance.current.stop()
      }

      instance.current = null
      levelListener.current = null
      levelDetector.current = null
    }
  }, [])

  // `ready` is included so this effect re-runs when the recorder becomes available,
  // allowing recording to start if record(true) was called before the recorder was ready.
  useEffect(() => {
    if (!instance.current) return

    if (isRecording === true) {
      instance.current.start()
      if (levelDetector.current) {
        levelListener.current = setInterval(() => {
            setLevel(levelDetector.current.getState())
        }, 500)
      }
    } else if (isRecording === false) {
      clearInterval(levelListener.current)
      instance.current.stop().then(onStop).catch(onError)
    }
  }, [isRecording, ready])

  return {
    ready,
    record,
    isRecording,
    level,
    analyserNode,
  }
}
